import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { PORTFOLIO, PORTFOLIO_GROUPS } from "../data/content";
import { ArrowUpRight, Play, Plus, Minus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const Card = ({ project, index, onPlay }) => {
  const hasVideo = Boolean(project.video);
  const hasLink = Boolean(project.link);
  const interactive = hasVideo || hasLink;

  const cls = `relative block w-full overflow-hidden rounded-sm border border-white/5 text-left ${project.ratio} ${
    interactive ? "cursor-pointer" : "cursor-default"
  }`;

  const inner = (
    <>
      <img
        src={project.image}
        alt={project.title}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-95" />

      {project.award && (
        <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full border border-gold/40 bg-black/50 px-3 py-1.5 text-[11px] uppercase tracking-luxury text-gold backdrop-blur-md">
          <span className="text-gold">★</span> Award Winner
        </div>
      )}

      {/* Corner indicator */}
      <div className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/30 text-white opacity-0 backdrop-blur-md transition-all duration-500 group-hover:opacity-100">
        {hasVideo ? <Play size={16} className="ml-0.5" /> : <ArrowUpRight size={18} />}
      </div>

      {hasVideo && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-black/30 text-white backdrop-blur-md transition-all duration-500 group-hover:scale-110 group-hover:border-gold/60">
            <Play size={22} className="ml-1" />
          </div>
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
        <div className="flex items-center gap-3 text-xs uppercase tracking-luxury text-gold">
          <span className="drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]">{project.category}</span>
          {project.year ? (
            <>
              <span className="h-px w-6 bg-gold/40" />
              <span className="text-zinc-300">{project.year}</span>
            </>
          ) : null}
        </div>
        <h3 className="mt-3 translate-y-1 font-serif text-2xl text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)] transition-transform duration-500 group-hover:translate-y-0 sm:text-3xl">
          {project.title}
        </h3>
      </div>
    </>
  );

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: (index % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative col-span-12 ${project.span}`}
      data-testid={`project-${project.id}`}
    >
      {hasVideo ? (
        <button
          type="button"
          onClick={() => onPlay(project)}
          className={cls}
          data-testid={`project-trigger-${project.id}`}
          aria-label={`Play ${project.title}`}
        >
          {inner}
        </button>
      ) : hasLink ? (
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className={cls}
          data-testid={`project-trigger-${project.id}`}
          aria-label={`View ${project.title} on Behance`}
        >
          {inner}
        </a>
      ) : (
        <div className={cls} data-testid={`project-trigger-${project.id}`}>
          {inner}
        </div>
      )}
    </motion.article>
  );
};

export const Portfolio = () => {
  const [active, setActive] = useState(null);
  const [group, setGroup] = useState("All");
  const [showAll, setShowAll] = useState(false);

  const INITIAL = 7;

  const filtered = useMemo(
    () =>
      group === "All"
        ? PORTFOLIO.projects
        : PORTFOLIO.projects.filter((p) => p.group === group),
    [group]
  );

  const visible = showAll ? filtered : filtered.slice(0, INITIAL);
  const hiddenCount = Math.max(filtered.length - INITIAL, 0);

  const changeGroup = (g) => {
    setGroup(g);
    setShowAll(false);
  };

  return (
    <section
      id="work"
      className="relative mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-40"
      data-testid="portfolio-section"
    >
      <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal>
            <Overline>{PORTFOLIO.overline}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              {PORTFOLIO.title}
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-zinc-400">{PORTFOLIO.intro}</p>
        </Reveal>
      </div>

      {/* Category tabs */}
      <Reveal delay={0.15}>
        <div
          className="mb-12 -mx-6 flex gap-x-8 gap-y-3 overflow-x-auto px-6 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0"
          data-testid="portfolio-tabs"
          role="tablist"
        >
          {PORTFOLIO_GROUPS.map((g) => {
            const isActive = group === g;
            const count =
              g === "All"
                ? PORTFOLIO.projects.length
                : PORTFOLIO.projects.filter((p) => p.group === g).length;
            return (
              <button
                key={g}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => changeGroup(g)}
                data-testid={`portfolio-tab-${g.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative shrink-0 border-b py-3 text-xs uppercase tracking-luxury transition-colors duration-300 sm:text-sm ${
                  isActive
                    ? "border-gold text-white"
                    : "border-transparent text-zinc-500 hover:text-zinc-200"
                }`}
              >
                <span className="flex items-baseline gap-2">
                  {g}
                  <span
                    className={`text-[10px] font-normal tracking-normal ${
                      isActive ? "text-gold" : "text-zinc-600"
                    }`}
                  >
                    ({count})
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </Reveal>

      <div className="grid grid-cols-12 items-start gap-5 lg:gap-6">
        <AnimatePresence mode="popLayout" initial={false}>
          {visible.map((p, i) => (
            <Card key={p.id} project={p} index={i} onPlay={setActive} />
          ))}
        </AnimatePresence>
      </div>

      {filtered.length === 0 && (
        <p className="py-16 text-center text-sm text-zinc-500" data-testid="portfolio-empty">
          No projects in this category yet.
        </p>
      )}

      {hiddenCount > 0 && (
        <div className="mt-16 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            data-testid="portfolio-toggle-more"
            className="group inline-flex items-center gap-3 border border-white/20 px-8 py-4 text-sm uppercase tracking-luxury text-white transition-colors duration-300 hover:bg-white hover:text-black"
          >
            {showAll ? (
              <>
                <Minus size={16} className="transition-transform duration-300 group-hover:rotate-180" />
                Show less
              </>
            ) : (
              <>
                <Plus size={16} className="transition-transform duration-300 group-hover:rotate-90" />
                View more work
                <span className="text-gold group-hover:text-black">
                  ({hiddenCount})
                </span>
              </>
            )}
          </button>
        </div>
      )}

      <Dialog open={Boolean(active)} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-5xl border-white/10 bg-[#0a0a0a] p-0"
          data-testid="project-video-modal"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">{active?.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {active?.category}
          </DialogDescription>
          {active?.video && (
            <div className="aspect-video w-full overflow-hidden rounded-sm bg-black">
              <iframe
                src={active.video}
                title={active.title}
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
                className="h-full w-full border-0"
                data-testid="project-video-iframe"
              />
            </div>
          )}
          <div className="flex items-baseline gap-3 px-5 pb-5 pt-1">
            <span className="text-xs uppercase tracking-luxury text-gold">
              {active?.category}
            </span>
            <span className="font-serif text-lg text-white">{active?.title}</span>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
